import { useEffect, useState } from 'react';
import { api, ago, clock, humanise, type ChatMessageRow } from '../api';
import { PageHead, Panel, State, Table, tripTone, useDebounced, useLoad } from '../ui';

/**
 * Every driver–rider chat. Support conversations are on the Support tab
 * (switch it to "Show resolved" for the history).
 */
export function Chats() {
  const [q, setQ] = useState('');
  const search = useDebounced(q);
  const [open, setOpen] = useState<string | null>(null);
  const { data, error, loading, reload } = useLoad(() => api.chats(search), { deps: [search], pollMs: 15000 });

  return (
    <>
      <PageHead
        title="Chats"
        blurb="Every message between drivers and riders, newest first. Support conversations are on the Support tab."
      />
      <div className="support">
        <Panel
          title={`${data?.length ?? 0} chats`}
          right={
            <>
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or phone…" style={{ width: 160 }} />
              <button className="btn sm" onClick={() => void reload()} disabled={loading}>Refresh</button>
            </>
          }
        >
          {error ? <div className="empty">{error}</div> : (
            <Table
              rows={data}
              keyOf={(c) => c.id}
              onRow={(c) => setOpen(c.id)}
              selected={open}
              empty="No messages yet."
              columns={[
                { head: 'Driver ↔ rider', cell: (c) => (<><div>{c.driver_name ?? '—'} ↔ {c.rider_name ?? 'whole trip'}</div><small className="detail">{c.last_message}</small></>) },
                { head: 'Ride', shrink: true, cell: (c) => (<><State tone={tripTone(c.trip_status)}>{humanise(c.trip_status)}</State><small className="detail">{c.service_date}</small></>) },
                { head: 'Msgs', num: true, shrink: true, cell: (c) => c.messages },
                { head: 'Last', shrink: true, cell: (c) => ago(c.last_at) },
              ]}
            />
          )}
        </Panel>
        {open ? <Thread id={open} /> : <Panel title="Messages"><div className="empty">Pick a chat.</div></Panel>}
      </div>
    </>
  );
}

function Thread({ id }: { id: string }) {
  const [msgs, setMsgs] = useState<ChatMessageRow[] | null>(null);
  useEffect(() => {
    setMsgs(null);
    api.chat(id).then((r) => setMsgs(r.messages)).catch(() => setMsgs([]));
  }, [id]);
  return (
    <Panel title="Messages">
      <div className="convo-pad">
        {!msgs ? <div className="empty">Loading…</div> : (
          <div className="messages">
            {msgs.map((m) => (
              <div key={m.id} className={`msg ${m.sender_role === 'driver' ? 'agent' : 'user'}`}>
                <span className="from">{m.sender_name ?? humanise(m.sender_role)} ({m.sender_role}) · {clock(m.sent_at)}{m.read_at ? ' · read' : ''}</span>
                <span>{m.body}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}
